'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Heading } from '@chakra-ui/react';
import RouterLink from 'next/link';
import React, { RefObject, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { ReactComponent as ArrowBackwardCircle } from '@/app/_icons/ArrowBackwardCircleOutline.svg';
// eslint-disable-next-line import/order
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleOutline.svg';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// import required modules
import { Range } from '@/types/request/request.types';
import { Special } from '@/types/specials/specials.types';

import AfterSalesPromotionCard from './after-sales-promotion-card/AfterSalesPromotionCard';
import styles from './AfterSalesPromotions.module.scss';

const titles: { [key: string]: string } = {
  'book-a-service': 'Service Promotions',
  'parts-and-accessories': 'Parts Promotions',
};

const specialFor: { [key: string]: string } = {
  'book-a-service': 'service',
  'parts-and-accessories': 'parts',
};

const AfterSalesPromotions = ({
  specials,
  slug,
  ranges,
}: {
  slug: string;
  specials: Special[];
  ranges: Range[];
}) => {
  const swiperRef: RefObject<SwiperRef> = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const CustomNavigation = () => {
    return (
      <div className={styles.servicePartsNavigation}>
        <button onClick={goPrev} className={styles.servicePartsSwiperPrev}>
          <ArrowBackwardCircle />
        </button>
        <button onClick={goNext} className={styles.servicePartsSwiperNext}>
          <ArrowForwardCircle />
        </button>
      </div>
    );
  };

  return (
    <>
      <Box className={styles.servicePartsWrapper} paddingY={'100px'}>
        <Container
          maxW={1280}
          width={'100%'}
          className={styles.servicePartsHeader}
          marginBottom={{ base: 16, md: 10 }}
          paddingX={{ base: 6, xl: 0 }}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            flexWrap={'wrap'}
            marginBottom={{ base: 16, md: 10 }}
          >
            <Box className={styles.headingElement} mb={8}>
              <RouterLink href={''} passHref style={{ textDecoration: 'none' }}>
                <Heading as="h2" className={styles.titleLg} color={'white'}>
                  {titles[slug]}
                </Heading>
              </RouterLink>
            </Box>
            <Box marginLeft={{ md: 'auto' }}>
              <LinkCustom
                aria-label="View All"
                href={`/promotions?specialFor=${specialFor[slug]}`}
                className="btnOutlineWhite"
              >
                View all
                <ChevronRightIcon width={4} height={4} marginLeft={3} />
              </LinkCustom>
            </Box>
          </Flex>
          <Swiper
            className={styles.servicePartsSlider}
            ref={swiperRef}
            slidesPerView={3}
            spaceBetween={40}
            modules={[Pagination, Navigation]}
            pagination={{
              el: styles.servicePartsNavigation,
              type: 'fraction',
              formatFractionCurrent: function(number) {
                return number < 10 ? '0' + number : number.toString();
              },
            }}
            navigation={{
              nextEl: styles.servicePartsSwiperNext,
              prevEl: styles.servicePartsSwiperPrev,
            }}
            onSlideChange={(swiper) => {
              setCurrentPage(swiper.realIndex + 1);
            }}
            onSwiper={(swiper) => {
              setTotalPages(swiper.slides.length);
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
                spaceBetween: 40,
              },
              576: {
                slidesPerView: 1,
                spaceBetween: 40,
              },
              640: {
                slidesPerView: 1,
                spaceBetween: 40,
              },
              641: {
                slidesPerView: 2,
                spaceBetween: 40,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
            }}
          >
            {specials?.map((service, i) => (
              <SwiperSlide key={i}>
                <AfterSalesPromotionCard
                  {...service}
                  type={slug}
                  ranges={ranges}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <Box className={styles.servicePartsSwiperControls}>
            <Box className={styles.servicePartsPagination}>
              <span className="current-page">{currentPage}</span>
              <span className="separator">/</span>
              <span className="total-pages">{totalPages}</span>
            </Box>
            <CustomNavigation />
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default AfterSalesPromotions;
